import type { ReactNode } from 'react';
import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { usePersistentIntersectionObserver } from '../../../_hooks/usePersistentIntersectionObserver';
import { EmptyTabState } from './EmptyTabState';

interface MemberFeedShellProps<T> {
  items: T[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
  renderItem: (item: T) => ReactNode;
  getItemKey: (item: T) => string;
  emptyHeading: string;
  /** Rendered while the first page is loading. */
  initialSkeleton: ReactNode;
  /** Rendered under the list while a subsequent page is loading. Defaults to none. */
  nextPageSkeleton?: ReactNode;
  /** Outer container classes (gap between rows, etc). */
  listClassName?: string;
}

/**
 * Shared infinite-scroll shell for member-profile feeds (Posts, Comments). Owns the sentinel +
 * IntersectionObserver, the empty / loading / error branches, and the retry button. Consumers
 * wire their hook output through and supply the row renderer + per-feed visual config.
 */
export const MemberFeedShell = <T,>({
  items,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  isError,
  renderItem,
  getItemKey,
  emptyHeading,
  initialSkeleton,
  nextPageSkeleton,
  listClassName = 'flex flex-col gap-6',
}: MemberFeedShellProps<T>) => {
  const { t } = useTranslation();
  const [sentinelRef, sentinelEntry] = usePersistentIntersectionObserver<HTMLDivElement>({ rootMargin: '400px' });

  // Read fetch state through a ref so the trigger effect re-runs only when the IntersectionObserver
  // emits a new entry — not when `isFetchingNextPage` flips. Otherwise, the moment a fetch completes
  // the effect re-runs with stale `isIntersecting === true` (IO callbacks lag behind React renders)
  // and cascades into back-to-back page fetches.
  const isFetchingRef = useRef(isFetchingNextPage);
  isFetchingRef.current = isFetchingNextPage;

  useEffect(() => {
    if (!sentinelEntry?.isIntersecting) return;
    if (!hasNextPage || isFetchingRef.current || isError) return;
    fetchNextPage();
  }, [sentinelEntry, hasNextPage, isError, fetchNextPage]);

  if (isLoading && items.length === 0) {
    return <>{initialSkeleton}</>;
  }

  if (items.length === 0) {
    return <EmptyTabState heading={emptyHeading} />;
  }

  return (
    <div className={listClassName}>
      {items.map((item) => (
        <Fragment key={getItemKey(item)}>{renderItem(item)}</Fragment>
      ))}
      {hasNextPage && !isError && <div ref={sentinelRef} className="h-1" />}
      {isFetchingNextPage && nextPageSkeleton}
      {isError && (
        <div className="flex justify-center py-8">
          <button
            type="button"
            className="text-xs text-wt-text-secondary hover:text-wt-text-primary"
            onClick={() => fetchNextPage()}
          >
            {t('community.feed.load_more_retry')}
          </button>
        </div>
      )}
    </div>
  );
};
